import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { DocumentModel, DocumentBlock, BlockType, DocumentSection, PageSettings, DocumentMetadata } from '../ast/document.types';
import { createSampleLabReport, createBlankDocument } from '../ast/factory';
import {
  HistoryState,
  createInitialHistory,
  pushHistory,
  undoHistory,
  redoHistory,
} from './historyManager';
import * as sectionOps from '../operations/sectionOps';
import * as blockOps from '../operations/blockOps';
import * as documentOps from '../operations/documentOps';
import { saveDocumentToDb, loadDocumentFromDb, getActiveDocumentIdFromDb } from '../persistence/indexedDbStorage';

export type SaveStatus = 'saved' | 'saving' | 'dirty';

interface DocumentContextType {
  document: DocumentModel;
  selectedSectionId: string | null;
  selectedBlockId: string | null;
  saveStatus: SaveStatus;
  canUndo: boolean;
  canRedo: boolean;
  
  // Selection
  setSelectedSectionId: (id: string | null) => void;
  setSelectedBlockId: (id: string | null) => void;

  // History Actions
  undo: () => void;
  redo: () => void;

  // Section Actions
  addSection: (title?: string, type?: DocumentSection['type'], targetIndex?: number) => void;
  removeSection: (sectionId: string) => void;
  updateSection: (sectionId: string, updates: Partial<Omit<DocumentSection, 'id'>>) => void;
  moveSection: (sectionId: string, direction: 'up' | 'down') => void;
  duplicateSection: (sectionId: string) => void;
  toggleSectionEnabled: (sectionId: string) => void;

  // Block Actions
  addBlock: (sectionId: string, blockOrType: DocumentBlock | BlockType, targetIndex?: number) => void;
  removeBlock: (sectionId: string, blockId: string) => void;
  updateBlockData: <T extends DocumentBlock>(sectionId: string, blockId: string, dataUpdates: Partial<T['data']>) => void;
  moveBlock: (sectionId: string, blockId: string, direction: 'up' | 'down') => void;
  duplicateBlock: (sectionId: string, blockId: string) => void;
  reorderBlockLayer: (sectionId: string, blockId: string, action: 'bringToFront' | 'sendToBack' | 'bringForward' | 'sendBackward') => void;
  nudgeBlock: (sectionId: string, blockId: string, dx: number, dy: number) => void;
  insertShape: (sectionId: string, shapeType: import('../ast/document.types').ShapeType, targetIndex?: number) => void;

  // Document Operations
  updateMetadata: (updates: Partial<DocumentMetadata>) => void;
  updatePageSettings: (updates: Partial<PageSettings>) => void;

  // Document Lifecycle
  loadDocument: (doc: DocumentModel) => void;
  newBlankDocument: (title?: string) => void;
  resetToSampleLabReport: () => void;
  manualSave: () => Promise<void>;
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

export const DocumentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [history, setHistory] = useState<HistoryState>(() => {
    return createInitialHistory(createSampleLabReport());
  });

  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');

  const autosaveTimeoutRef = useRef<number | null>(null);

  // Initialize from IndexedDB on startup
  useEffect(() => {
    async function initFromStorage() {
      try {
        const activeId = await getActiveDocumentIdFromDb();
        if (activeId) {
          const loaded = await loadDocumentFromDb(activeId);
          if (loaded) {
            setHistory(createInitialHistory(loaded));
            if (loaded.sections.length > 0) {
              setSelectedSectionId(loaded.sections[0].id);
            }
          }
        }
      } catch (err) {
        console.warn('Could not restore previous document from IndexedDB:', err);
      }
    }
    initFromStorage();
  }, []);

  // Update document helper (pushes to history + triggers autosave)
  const commitDocumentChange = useCallback((updater: (doc: DocumentModel) => DocumentModel) => {
    setHistory((prev) => {
      const nextDoc = updater(prev.present);
      return pushHistory(prev, nextDoc);
    });
    setSaveStatus('dirty');
  }, []);

  // Debounced Autosave to IndexedDB
  useEffect(() => {
    if (saveStatus !== 'dirty') return;

    if (autosaveTimeoutRef.current) {
      window.clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = window.setTimeout(async () => {
      setSaveStatus('saving');
      try {
        await saveDocumentToDb(history.present);
        setSaveStatus('saved');
      } catch (e) {
        console.error('Autosave failed:', e);
        setSaveStatus('dirty');
      }
    }, 600);

    return () => {
      if (autosaveTimeoutRef.current) {
        window.clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [history.present, saveStatus]);

  // Undo / Redo
  const undo = useCallback(() => {
    setHistory((prev) => undoHistory(prev));
    setSaveStatus('dirty');
  }, []);

  const redo = useCallback(() => {
    setHistory((prev) => redoHistory(prev));
    setSaveStatus('dirty');
  }, []);

  // Section Action Implementations
  const addSection = useCallback((title?: string, type?: DocumentSection['type'], targetIndex?: number) => {
    commitDocumentChange((doc) => sectionOps.addSection(doc, title, type, targetIndex));
  }, [commitDocumentChange]);

  const removeSection = useCallback((sectionId: string) => {
    commitDocumentChange((doc) => sectionOps.removeSection(doc, sectionId));
  }, [commitDocumentChange]);

  const updateSection = useCallback((sectionId: string, updates: Partial<Omit<DocumentSection, 'id'>>) => {
    commitDocumentChange((doc) => sectionOps.updateSection(doc, sectionId, updates));
  }, [commitDocumentChange]);

  const moveSection = useCallback((sectionId: string, direction: 'up' | 'down') => {
    commitDocumentChange((doc) => sectionOps.moveSection(doc, sectionId, direction));
  }, [commitDocumentChange]);

  const duplicateSection = useCallback((sectionId: string) => {
    commitDocumentChange((doc) => sectionOps.duplicateSection(doc, sectionId));
  }, [commitDocumentChange]);

  const toggleSectionEnabled = useCallback((sectionId: string) => {
    commitDocumentChange((doc) => sectionOps.toggleSectionEnabled(doc, sectionId));
  }, [commitDocumentChange]);

  // Block Action Implementations
  const addBlock = useCallback((sectionId: string, blockOrType: DocumentBlock | BlockType, targetIndex?: number) => {
    commitDocumentChange((doc) => blockOps.addBlock(doc, sectionId, blockOrType, targetIndex));
  }, [commitDocumentChange]);

  const removeBlock = useCallback((sectionId: string, blockId: string) => {
    commitDocumentChange((doc) => blockOps.removeBlock(doc, sectionId, blockId));
  }, [commitDocumentChange]);

  const updateBlockData = useCallback(<T extends DocumentBlock>(sectionId: string, blockId: string, dataUpdates: Partial<T['data']>) => {
    commitDocumentChange((doc) => blockOps.updateBlockData(doc, sectionId, blockId, dataUpdates));
  }, [commitDocumentChange]);

  const moveBlock = useCallback((sectionId: string, blockId: string, direction: 'up' | 'down') => {
    commitDocumentChange((doc) => blockOps.moveBlock(doc, sectionId, blockId, direction));
  }, [commitDocumentChange]);

  const duplicateBlock = useCallback((sectionId: string, blockId: string) => {
    commitDocumentChange((doc) => blockOps.duplicateBlock(doc, sectionId, blockId));
  }, [commitDocumentChange]);

  const reorderBlockLayer = useCallback((sectionId: string, blockId: string, action: 'bringToFront' | 'sendToBack' | 'bringForward' | 'sendBackward') => {
    commitDocumentChange((doc) => blockOps.reorderBlockLayer(doc, sectionId, blockId, action));
  }, [commitDocumentChange]);

  const nudgeBlock = useCallback((sectionId: string, blockId: string, dx: number, dy: number) => {
    commitDocumentChange((doc) => blockOps.nudgeBlock(doc, sectionId, blockId, dx, dy));
  }, [commitDocumentChange]);

  const insertShape = useCallback((sectionId: string, shapeType: import('../ast/document.types').ShapeType, targetIndex?: number) => {
    const shapeBlock = blockOps.createShapeBlock(shapeType);
    commitDocumentChange((doc) => blockOps.addBlock(doc, sectionId, shapeBlock, targetIndex));
  }, [commitDocumentChange]);

  // Document Operations
  const updateMetadata = useCallback((updates: Partial<DocumentMetadata>) => {
    commitDocumentChange((doc) => documentOps.updateMetadata(doc, updates));
  }, [commitDocumentChange]);

  const updatePageSettings = useCallback((updates: Partial<PageSettings>) => {
    commitDocumentChange((doc) => documentOps.updatePageSettings(doc, updates));
  }, [commitDocumentChange]);

  // Lifecycle
  const loadDocument = useCallback((doc: DocumentModel) => {
    setHistory(createInitialHistory(doc));
    setSaveStatus('saved');
    saveDocumentToDb(doc);
  }, []);

  const newBlankDocument = useCallback((title?: string) => {
    const blank = createBlankDocument(title);
    setHistory(createInitialHistory(blank));
    setSaveStatus('dirty');
  }, []);

  const resetToSampleLabReport = useCallback(() => {
    const sample = createSampleLabReport();
    setHistory(createInitialHistory(sample));
    setSaveStatus('dirty');
  }, []);

  const manualSave = useCallback(async () => {
    setSaveStatus('saving');
    try {
      await saveDocumentToDb(history.present);
      setSaveStatus('saved');
    } catch {
      setSaveStatus('dirty');
    }
  }, [history.present]);

  return (
    <DocumentContext.Provider
      value={{
        document: history.present,
        selectedSectionId,
        selectedBlockId,
        saveStatus,
        canUndo: history.canUndo,
        canRedo: history.canRedo,
        setSelectedSectionId,
        setSelectedBlockId,
        undo,
        redo,
        addSection,
        removeSection,
        updateSection,
        moveSection,
        duplicateSection,
        toggleSectionEnabled,
        addBlock,
        removeBlock,
        updateBlockData,
        moveBlock,
        duplicateBlock,
        reorderBlockLayer,
        nudgeBlock,
        insertShape,
        updateMetadata,
        updatePageSettings,
        loadDocument,
        newBlankDocument,
        resetToSampleLabReport,
        manualSave,
      }}
    >
      {children}
    </DocumentContext.Provider>
  );
};

export function useDocument(): DocumentContextType {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocument must be used within a DocumentProvider');
  }
  return context;
}
